/**
 * Landing image overrides — fetch, resolve and replace.
 *
 * Provides every landing section with a `src` for an image slot and `text` for
 * a copy key, and — for signed-in admins only — the ability to change either.
 * Overrides live in the DB; anything without one falls back to the bundled
 * asset or wording, so the page renders identically on a fresh install.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiHeaders, config } from '../../../auth/config';
import { useAuth } from '../../../auth';
import { isAdmin } from '../../../../shared/utils';
import { slotFallback, slotSpec, type LandingSlot } from './landingImages';

const IMAGES_ENDPOINT = '/api/landing/images';
const CONTENT_ENDPOINT = '/api/landing/content';

/** Storage returns root-relative URLs (`/uploads/...`) served by the API host. */
function absolute(url: string): string {
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:')) return url;
  return `${config.api.baseUrl.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
}

function authHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers[config.auth.headers.auth] = `Bearer ${token}`;
  return apiHeaders(headers);
}

interface LandingImagesValue {
  /** Resolved copy for a key — override if present, bundled default otherwise. */
  text: (key: string, fallback: string) => string;
  /** Replace a piece of copy. */
  setText: (key: string, value: string) => Promise<void>;
  /** Drop the copy override and go back to the bundled wording. */
  resetText: (key: string) => Promise<void>;
  /** Whether a key currently has an admin-edited override. */
  isTextOverridden: (key: string) => boolean;
  /** Key currently saving, so only that field shows progress. */
  savingKey: string | null;
  /** Resolved URL for a slot — override if present, bundled art otherwise. */
  src: (slot: LandingSlot) => string;
  /** True when the signed-in user may replace images. */
  canEdit: boolean;
  /** Slot currently uploading, so the UI can show progress on just that one. */
  savingSlot: LandingSlot | null;
  /** Replace a slot's image. Resolves once the new URL is live. */
  replace: (slot: LandingSlot, file: File) => Promise<void>;
  /** Drop the override and go back to the bundled art. */
  reset: (slot: LandingSlot) => Promise<void>;
  /** Whether a slot currently has an admin-uploaded override. */
  isOverridden: (slot: LandingSlot) => boolean;
  error: string | null;
  clearError: () => void;
}

const LandingImagesContext = createContext<LandingImagesValue | null>(null);

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    return body?.error || body?.message || fallback;
  } catch {
    return fallback;
  }
}

export const LandingImagesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [copy, setCopy] = useState<Record<string, string>>({});
  const [savingSlot, setSavingSlot] = useState<LandingSlot | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canEdit = Boolean(token) && isAdmin(user);

  // Load overrides once. A failure here is non-fatal: the page still renders
  // with bundled art, so it's logged rather than surfaced to visitors.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${config.api.baseUrl}${IMAGES_ENDPOINT}`, { headers: apiHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const img of body.images ?? []) next[img.slot] = absolute(img.url);
        setOverrides(next);
      } catch (err) {
        if (!cancelled) {
          console.warn('[landing] could not load image overrides, using bundled art', err);
        }
      }

      try {
        const res = await fetch(`${config.api.baseUrl}${CONTENT_ENDPOINT}`, { headers: apiHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const row of body.content ?? []) next[row.key] = row.value;
        setCopy(next);
      } catch (err) {
        if (!cancelled) {
          console.warn('[landing] could not load copy overrides, using bundled text', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const src = useCallback(
    (slot: LandingSlot) => overrides[slot] ?? slotFallback(slot),
    [overrides],
  );

  const isOverridden = useCallback((slot: LandingSlot) => Boolean(overrides[slot]), [overrides]);

  const replace = useCallback(
    async (slot: LandingSlot, file: File) => {
      setSavingSlot(slot);
      setError(null);
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const res = await fetch(`${config.api.baseUrl}${IMAGES_ENDPOINT}/${slot}`, {
          method: 'PUT',
          headers: authHeaders(token),
          body: JSON.stringify({ dataUrl, filename: file.name }),
        });
        if (!res.ok) throw new Error(await readError(res, `Upload failed (HTTP ${res.status})`));
        const body = await res.json();
        setOverrides((prev) => ({ ...prev, [slot]: absolute(body.image.url) }));
      } catch (err: any) {
        setError(err?.message ?? 'Upload failed');
        throw err;
      } finally {
        setSavingSlot(null);
      }
    },
    [token],
  );

  const reset = useCallback(
    async (slot: LandingSlot) => {
      setSavingSlot(slot);
      setError(null);
      try {
        const res = await fetch(`${config.api.baseUrl}${IMAGES_ENDPOINT}/${slot}`, {
          method: 'DELETE',
          headers: authHeaders(token),
        });
        if (!res.ok) throw new Error(await readError(res, `Reset failed (HTTP ${res.status})`));
        setOverrides((prev) => {
          const next = { ...prev };
          delete next[slot];
          return next;
        });
      } catch (err: any) {
        setError(err?.message ?? 'Reset failed');
        throw err;
      } finally {
        setSavingSlot(null);
      }
    },
    [token],
  );

  const text = useCallback(
    (key: string, fallback: string) => copy[key] ?? fallback,
    [copy],
  );

  const isTextOverridden = useCallback((key: string) => key in copy, [copy]);

  const setText = useCallback(
    async (key: string, next: string) => {
      setSavingKey(key);
      setError(null);
      try {
        const res = await fetch(`${config.api.baseUrl}${CONTENT_ENDPOINT}/${key}`, {
          method: 'PUT',
          headers: authHeaders(token),
          body: JSON.stringify({ value: next }),
        });
        if (!res.ok) throw new Error(await readError(res, `Save failed (HTTP ${res.status})`));
        const body = await res.json();
        setCopy((prev) => ({ ...prev, [key]: body.content.value }));
      } catch (err: any) {
        setError(err?.message ?? 'Save failed');
        throw err;
      } finally {
        setSavingKey(null);
      }
    },
    [token],
  );

  const resetText = useCallback(
    async (key: string) => {
      setSavingKey(key);
      setError(null);
      try {
        const res = await fetch(`${config.api.baseUrl}${CONTENT_ENDPOINT}/${key}`, {
          method: 'DELETE',
          headers: authHeaders(token),
        });
        if (!res.ok) throw new Error(await readError(res, `Reset failed (HTTP ${res.status})`));
        setCopy((prev) => {
          const nextCopy = { ...prev };
          delete nextCopy[key];
          return nextCopy;
        });
      } catch (err: any) {
        setError(err?.message ?? 'Reset failed');
        throw err;
      } finally {
        setSavingKey(null);
      }
    },
    [token],
  );

  const value = useMemo<LandingImagesValue>(
    () => ({
      text,
      setText,
      resetText,
      isTextOverridden,
      savingKey,
      src,
      canEdit,
      savingSlot,
      replace,
      reset,
      isOverridden,
      error,
      clearError: () => setError(null),
    }),
    [
      text, setText, resetText, isTextOverridden, savingKey,
      src, canEdit, savingSlot, replace, reset, isOverridden, error,
    ],
  );

  return <LandingImagesContext.Provider value={value}>{children}</LandingImagesContext.Provider>;
};

export function useLandingImages(): LandingImagesValue {
  const ctx = useContext(LandingImagesContext);
  if (!ctx) throw new Error('useLandingImages must be used within a LandingImagesProvider');
  return ctx;
}

/** Label + hint for a slot, for the admin overlay. */
export function slotMeta(slot: LandingSlot) {
  return slotSpec(slot);
}
